import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  CheckCircle2, 
  Loader2, 
  Building2,
  Mail,
  Phone,
  FileText,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Lock,
  Landmark,
  CreditCard,
  Receipt,
  BanknoteIcon,
  Copy,
  User,
  MapPin,
  Globe,
  Hash
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bank account details
const BANK_DETAILS = {
  beneficiary: "CESAR ALEJANDRO AMAYA HERNANDEZ",
  accountType: "Ahorros",
  accountNumber: "0026960279",
  bank: "Banco Guayaquil S.A",
  address: "Pichincha 105 y P. Icaza, Guayaquil – Ecuador",
  swift: "GUAYECEGXXX",
  postalCode: "090306",
  bankPhone: "+593 4 3730100",
  id: "1721184842"
};

const BANKS = [
  "Banco Pichincha",
  "Banco Guayaquil",
  "Banco del Pacífico",
  "Produbanco",
  "Banco Internacional",
  "Banco Bolivariano",
  "Banco del Austro",
  "Cooperativa JEP",
  "Banco General Rumiñahui",
  "Banco Solidario",
  "BanEcuador",
  "Otro"
];

const TransferPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: bank info, 2: form, 3: success
  const [isLoading, setIsLoading] = useState(false);
  const [saleData, setSaleData] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    project_name: '',
    contact_email: '',
    contact_phone: '',
    amount: '',
    transfer_voucher: '',
    transfer_bank: '',
    transfer_account: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBankChange = (value) => {
    setFormData(prev => ({ ...prev, transfer_bank: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/sales`, {
        ...formData,
        amount: parseFloat(formData.amount),
        currency: 'USD',
        payment_method: 'transfer'
      });
      
      setSaleData(response.data);
      setStep(3);
      toast.success('¡Transferencia registrada exitosamente!');
    } catch (error) {
      console.error('Error registering transfer:', error);
      toast.error('Error al registrar la transferencia');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  const currentDate = new Date().toLocaleDateString('es-EC', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2F3D8E] via-[#1a2456] to-[#2F3D8E]">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver al inicio</span>
          </button>
          <div className="flex items-center gap-2 text-white">
            <Lock className="w-4 h-4 text-[#5DA5D8]" />
            <span className="text-sm">Registro Seguro</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${
                  step >= s ? 'text-white' : 'text-white/40'
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step > s ? 'bg-[#5DA5D8]' : step === s ? 'bg-[#5DA5D8]' : 'bg-white/20'
                  }`}>
                    {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {s === 1 ? 'Datos Bancarios' : s === 2 ? 'Registro' : 'Confirmación'}
                  </span>
                </div>
                {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-[#5DA5D8]' : 'bg-white/20'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Bank Information */}
        {step === 1 && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#5DA5D8] to-[#2F3D8E] p-8 text-white text-center">
              <Landmark className="w-12 h-12 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">
                Datos para Transferencia Bancaria
              </h2>
              <p className="opacity-90 text-lg">
                Realiza tu transferencia a la siguiente cuenta y luego registra tu pago
              </p>
            </div>

            <div className="p-8">
              {/* Bank Details Card */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border-2 border-[#5DA5D8]/30 mb-8">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Beneficiary */}
                  <div className="md:col-span-2 bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#2F3D8E]/10 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-[#2F3D8E]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Beneficiario</p>
                          <p className="text-lg font-bold text-[#2F3D8E]">{BANK_DETAILS.beneficiary}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.beneficiary, 'Beneficiario')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Account Number */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#4C8C3B]/10 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-[#4C8C3B]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Cuenta {BANK_DETAILS.accountType}</p>
                          <p className="text-xl font-bold text-[#4C8C3B] font-mono tracking-wider">{BANK_DETAILS.accountNumber}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.accountNumber, 'Número de cuenta')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Bank */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#5DA5D8]/10 rounded-lg flex items-center justify-center">
                          <Landmark className="w-5 h-5 text-[#5DA5D8]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Banco Receptor</p>
                          <p className="text-lg font-bold text-[#2F3D8E]">{BANK_DETAILS.bank}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.bank, 'Banco')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* ID */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Cédula / ID</p>
                          <p className="text-lg font-bold text-[#2F3D8E] font-mono">{BANK_DETAILS.id}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.id, 'ID')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* SWIFT */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Código SWIFT</p>
                          <p className="text-lg font-bold text-[#2F3D8E] font-mono">{BANK_DETAILS.swift}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.swift, 'SWIFT')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="md:col-span-2 bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Dirección</p>
                          <p className="text-base font-semibold text-[#2F3D8E]">{BANK_DETAILS.address}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.address, 'Dirección')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Bank Phone */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Teléfono del Banco</p>
                          <p className="text-lg font-bold text-[#2F3D8E]">{BANK_DETAILS.bankPhone}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.bankPhone, 'Teléfono')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Postal Code */}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Código Postal</p>
                          <p className="text-lg font-bold text-[#2F3D8E] font-mono">{BANK_DETAILS.postalCode}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(BANK_DETAILS.postalCode, 'Código Postal')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
                <h4 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Instrucciones
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-yellow-700">
                  <li>Realiza la transferencia bancaria a la cuenta indicada arriba</li>
                  <li>Guarda el número de comprobante de tu transferencia</li>
                  <li>Haz clic en "Ya realicé mi transferencia" para registrar tu pago</li>
                  <li>Nuestro equipo verificará tu pago en las próximas horas</li>
                </ol>
              </div>

              {/* CTA Button */}
              <Button
                onClick={() => setStep(2)}
                className="w-full h-14 bg-[#4C8C3B] hover:bg-[#3d7030] text-white text-lg font-semibold rounded-xl shadow-lg shadow-[#4C8C3B]/30 transition-all duration-300 hover:shadow-xl"
              >
                Ya realicé mi transferencia
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-center text-sm text-gray-400 mt-4">
                Haz clic en el ícono <Copy className="w-4 h-4 inline" /> para copiar cualquier dato
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Form */}
        {step === 2 && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Sales Message */}
            <div className="bg-gradient-to-r from-[#5DA5D8] to-[#2F3D8E] p-6 text-white text-center">
              <Landmark className="w-8 h-8 mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-2">
                Registro de Transferencia Bancaria
              </h2>
              <p className="opacity-90">
                Ingresa los datos de tu transferencia para validar tu pago de forma segura.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-[#2F3D8E]">Información del Pago</h3>
                <p className="text-gray-500 mt-2">Fecha: {currentDate}</p>
              </div>

              {/* Transfer Details Section */}
              <div className="bg-[#5DA5D8]/10 rounded-2xl p-6 mb-6">
                <h4 className="text-lg font-bold text-[#2F3D8E] mb-4 flex items-center gap-2">
                  <BanknoteIcon className="w-5 h-5" />
                  Datos de la Transferencia
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <Receipt className="w-4 h-4" />
                      Número de Voucher / Comprobante
                    </Label>
                    <Input
                      name="transfer_voucher"
                      value={formData.transfer_voucher}
                      onChange={handleChange}
                      placeholder="Ej: 123456789"
                      required
                      className="h-12 rounded-xl font-mono text-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <Landmark className="w-4 h-4" />
                      Banco de Origen
                    </Label>
                    <Select onValueChange={handleBankChange} required>
                      <SelectTrigger className="h-12 rounded-xl">
                        <SelectValue placeholder="Selecciona el banco" />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKS.map((bank) => (
                          <SelectItem key={bank} value={bank}>
                            {bank}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <CreditCard className="w-4 h-4" />
                      Número de Cuenta de Origen
                    </Label>
                    <Input
                      name="transfer_account"
                      value={formData.transfer_account}
                      onChange={handleChange}
                      placeholder="Ej: 2200XXXXXX"
                      required
                      className="h-12 rounded-xl font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-[#2F3D8E]">
                  <BanknoteIcon className="w-4 h-4" />
                  Monto Transferido (USD)
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold">$</span>
                  <Input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    required
                    className="h-14 rounded-xl pl-10 text-2xl font-bold text-[#2F3D8E]"
                  />
                </div>
              </div>

              {/* Company Details */}
              <div className="border-t pt-6 mt-6">
                <h4 className="text-lg font-bold text-[#2F3D8E] mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Información de la Empresa
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <Building2 className="w-4 h-4" />
                      Nombre de la Empresa
                    </Label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="Ej: Empresa ABC S.A."
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <FileText className="w-4 h-4" />
                      Nombre del Proyecto
                    </Label>
                    <Input
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleChange}
                      placeholder="Ej: Sistema de Inventarios"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <Mail className="w-4 h-4" />
                      Correo de Contacto
                    </Label>
                    <Input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      placeholder="correo@empresa.com"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-[#2F3D8E]">
                      <Phone className="w-4 h-4" />
                      Teléfono de Contacto
                    </Label>
                    <Input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="+593 999 999 999"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 bg-[#5DA5D8] hover:bg-[#4a94c7] text-white text-lg font-semibold rounded-xl shadow-lg shadow-[#5DA5D8]/30 transition-all duration-300 hover:shadow-xl"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Registrando...</>
                ) : (
                  <><Landmark className="mr-2 h-5 w-5" /> Registrar Transferencia</>
                )}
              </Button>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Registro Seguro</span>
                </div>
                <div className="flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  <span>Datos Protegidos</span>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && saleData && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#5DA5D8] to-[#2F3D8E] p-8 text-white text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12" />
              </div>
              <h2 className="text-3xl font-bold mb-2">¡Transferencia Registrada!</h2>
              <p className="opacity-90 text-lg">Tu pago está pendiente de verificación</p>
            </div>

            <div className="p-8 text-center">
              {/* Digital Voucher */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8 border-2 border-dashed border-[#5DA5D8]">
                <p className="text-sm text-gray-500 mb-2">VOUCHER ANALYTICA</p>
                <p className="text-4xl font-bold text-[#2F3D8E] tracking-wider mb-4">
                  {saleData.voucher_number}
                </p>
                <div className="w-16 h-1 bg-[#5DA5D8] mx-auto mb-4" />
                
                <div className="grid grid-cols-2 gap-4 text-left max-w-md mx-auto">
                  <div>
                    <p className="text-xs text-gray-400">EMPRESA</p>
                    <p className="font-semibold text-[#2F3D8E]">{saleData.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">PROYECTO</p>
                    <p className="font-semibold text-[#2F3D8E]">{saleData.project_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">MONTO</p>
                    <p className="font-bold text-[#5DA5D8] text-xl">${saleData.amount.toFixed(2)} USD</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">ESTADO</p>
                    <p className="font-semibold text-yellow-600">⏳ PENDIENTE VERIFICACIÓN</p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-dashed border-gray-300">
                  <p className="text-xs text-gray-400 mb-2">DATOS DE TRANSFERENCIA</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Voucher</p>
                      <p className="font-mono font-bold text-[#2F3D8E]">{saleData.transfer_voucher}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Banco</p>
                      <p className="font-semibold text-[#2F3D8E]">{saleData.transfer_bank}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Cuenta</p>
                      <p className="font-mono font-semibold text-[#2F3D8E]">{saleData.transfer_account}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
                <Sparkles className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-[#2F3D8E] mb-2">
                  ¿Qué sigue?
                </h3>
                <p className="text-gray-600">
                  Nuestro equipo de finanzas verificará tu transferencia en las próximas horas. 
                  Una vez confirmado el pago, nos pondremos en contacto contigo para iniciar tu proyecto.
                </p>
              </div>

              <p className="text-sm text-gray-400 mb-6">
                Hemos enviado los detalles a nuestro equipo de finanzas para verificación.
              </p>

              <Button
                onClick={() => navigate('/')}
                className="bg-[#2F3D8E] hover:bg-[#1a2456] text-white px-8 py-3 rounded-xl"
              >
                Volver al Inicio
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferPage;
